/**
 * Dashboard API client methods.
 */

import { API_CONFIG } from '../config/api';
import { getToken } from './tokenStorage';
import { logApiRequest, logApiResponse, logApiError } from '../utils/errorHandler';

export interface DashboardData {
  total_value: number;
  total_value_base_currency: number;
  base_currency: string;
  currency_breakdown: CurrencyBreakdown[];
  total_positions: number;
  positions_with_thesis: number;
  thesis_coverage: number;
  sector_breakdown: SectorBreakdown[];
  geographic_breakdown: GeographicBreakdown[];
  asset_category_breakdown: AssetCategoryBreakdown[];
  strategy_breakdown: StrategyBreakdown[];
  /**
   * Day-over-day change of the open book in base currency (spec §6.8): Σ
   * qty × (last close − prev close), FX-normalized. Symbols without two
   * recent closes are omitted from the figure and listed in
   * `excluded_symbols` instead of silently skewing it. Optional only because
   * responses cached before Task 4 predate the keys.
   */
  day_change_base?: number;
  /** Symbols excluded from `day_change_base` for stale or missing prices. */
  excluded_symbols?: string[];
}

export interface CurrencyBreakdown {
  currency: string;
  value: number;
  value_base_currency: number;
  percentage: number;
  position_count: number;
}

export interface SectorBreakdown {
  sector: string;
  value: number;
  percentage: number;
  position_count: number;
}

export interface GeographicBreakdown {
  country: string;
  value: number;
  percentage: number;
  position_count: number;
  currency: string;
}

export interface AssetCategoryBreakdown {
  asset_category: string;
  value: number;
  percentage: number;
  position_count: number;
  currency: string;
}

export interface StrategyBreakdown {
  strategy: string;
  value: number;
  percentage: number;
  position_count: number;
}

/**
 * Get dashboard data including portfolio total, thesis coverage, and breakdowns.
 */
export async function getDashboardData(): Promise<DashboardData> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/dashboard`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to load dashboard' }));
      const error = new Error(errorData.detail || 'Failed to load dashboard');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}
